import * as React from "react";
import { DayPicker } from "react-day-picker";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: React.ComponentProps<typeof DayPicker>) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      classNames={{
        months: "flex flex-col sm:flex-row gap-2 relative",
        month: "flex flex-col gap-4",
        /* No react-day-picker v9 o <nav> é IRMÃO do caption, não filho: setas
           com `absolute` se ancoravam no root e caíam acima da linha do mês.
           Aqui o nav vira a própria faixa do cabeçalho (mesma altura h-7 do
           caption, sobreposta a ele), com as setas nas pontas e o rótulo
           centralizado por baixo. */
        month_caption: "flex h-7 w-full items-center justify-center",
        caption_label: "text-sm font-medium leading-none select-none text-[var(--color-text)]",
        nav: "absolute top-0 left-0 right-0 z-10 flex h-7 items-center justify-between",
        button_previous: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 shrink-0 bg-transparent p-0 text-[var(--color-text-muted)] hover:text-[var(--color-text)] border-[var(--color-border)]"
        ),
        button_next: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 shrink-0 bg-transparent p-0 text-[var(--color-text-muted)] hover:text-[var(--color-text)] border-[var(--color-border)]"
        ),
        month_grid: "w-full border-collapse",
        weekdays: "flex",
        weekday:
          "text-[var(--color-text-muted)] rounded-md w-8 font-normal text-[0.8rem]",
        week: "flex w-full mt-2",
        day: cn(
          "relative p-0 text-center text-sm text-[var(--color-text)] focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-[var(--color-primary)]/10 [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-[var(--color-primary)]/5",
          props.mode === "range"
            ? "[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md"
            : "[&:has([aria-selected])]:rounded-md"
        ),
        day_button: cn(
          "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors",
          "h-8 w-8 p-0 font-normal text-inherit hover:bg-[var(--color-primary)]/10 hover:text-inherit focus-visible:ring-[var(--color-primary)]/30"
        ),
        range_end: "day-range-end",
        selected:
          "bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary)] hover:text-white focus:bg-[var(--color-primary)] focus:text-white rounded-md",
        today: "bg-[var(--color-primary)]/10 text-[var(--color-primary)] font-semibold rounded-md",
        outside:
          "day-outside text-[var(--color-text-muted)] opacity-50 aria-selected:bg-[var(--color-primary)]/5 aria-selected:text-[var(--color-text-muted)]",
        disabled: "text-[var(--color-text-muted)] opacity-50",
        range_middle:
          "aria-selected:bg-[var(--color-primary)]/10 aria-selected:text-[var(--color-text)]",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) => {
          const Icon = orientation === "left" ? ChevronLeft : ChevronRight;
          return <Icon className="h-4 w-4" />;
        },
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
